import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Container from "layouts/container/Container";
import { requester } from "App";
import { AuthReducer } from "shared/redux/reducers/auth/AuthReducer";
import { getTokenPayload } from "shared/utils/Utils";
import { devLog } from "utils/logger";

function CardAuthVerification() {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    // MobileNumberSignIn에서 전달된 state 사용
    const urlParams = location.state || {
        deviceCode: null,
        storeCode: null,
        type: null
    };

    // Redux에서 token 가져오기
    const { token } = useSelector((state) => ({
        token: state.auth.token,
    }));

    // 카드 인증 처리 상태
    const [cardAuthStatus, setCardAuthStatus] = useState({
        loading: false,
        step: 'init', // 'init', 'processing', 'success', 'error'
        message: ''
    });

    // 웹뷰 체크 함수
    const isOurAppWebView = () => {
        const userAgent = navigator.userAgent.toLowerCase();
        const isWebView = (() => {
            const isIOSWebView = /(iphone|ipod|ipad).*applewebkit(?!.*safari)/i.test(userAgent);
            const isAndroidWebView = /wv/.test(userAgent) || /android.*samsungbrowser\/(?!.*chrome)/i.test(userAgent);
            return isIOSWebView || isAndroidWebView;
        })();

        if (isWebView) {
            if (window.BRIDGE && window.BRIDGE.isOurAppWebView && window.BRIDGE.isOurAppWebView()) {
                return true;
            } else if (window.webkit?.messageHandlers?.BRIDGE) {
                return true;
            }
        }
        return false;
    };

    // 창 닫기 함수
    const closeWebView = () => {
        if (isOurAppWebView()) {
            if (window.BRIDGE?.closeWebView) {
                window.BRIDGE.closeWebView();
            } else if (window.webkit?.messageHandlers?.BRIDGE) {
                window.webkit.messageHandlers.BRIDGE.postMessage('closeWebView');
            }
        } else {
            try {
                window.close();
            } catch (error) {
                devLog('브라우저에서 창을 닫을 수 없습니다.');
            }
        }
    };

    // 앱에서 토큰 가져오기 (Promise 기반) - DoorAccessGateway와 동일
    const getTokenFromApp = () => {
        return new Promise((resolve, reject) => {
            // 타임아웃 설정 (5초)
            const timeoutId = setTimeout(() => {
                if (window.handleTokenResponse) {
                    delete window.handleTokenResponse;
                }
                reject(new Error("토큰 요청 시간 초과"));
            }, 5000);

            // iOS만 처리 
            if (window.webkit?.messageHandlers?.BRIDGE) {
                // iOS 네이티브에서 호출할 전역 콜백 함수
                window.handleTokenResponse = (token) => {
                    clearTimeout(timeoutId);
                    if (token) {
                        resolve(token);
                    } else {
                        reject(new Error("iOS에서 토큰을 가져오지 못했습니다."));
                    }
                    delete window.handleTokenResponse;
                };
                window.webkit.messageHandlers.BRIDGE.postMessage({ command: "getToken" });
            }
            // 웹뷰 환경이 아님
            else {
                clearTimeout(timeoutId);
                reject(new Error("앱 웹뷰 환경이 아닙니다."));
            }
        });
    };

    // 카드 인증 처리 함수
    const handleCardAuth = async () => {
        setCardAuthStatus({
            loading: true,
            step: 'processing',
            message: '앱에서 정보를 가져오는 중입니다...'
        });

        try {
            // DoorAccessGateway와 동일한 패턴: Redux 토큰 우선 확인 후 없으면 앱에서 가져오기
            let appToken = token; // Redux에 이미 토큰이 있을 수 있음

            if (!appToken) {
                appToken = await getTokenFromApp();
                dispatch(AuthReducer.actions.setToken(appToken));

                devLog("앱에서 토큰 획득 성공");
            }

            // userId 추출 (DoorAccessGateway에서 사용하는 방식과 동일)
            const userId = getTokenPayload(appToken).userId;

            console.log('추출된 userId:', userId);
            // toast.success(`userId 확인: ${userId}`, {
            //     autoClose: 3000,
            //     position: toast.POSITION.BOTTOM_CENTER
            // });

            setCardAuthStatus({
                loading: true,
                step: 'processing',
                message: '성인인증을 확인하고 있어요...'
            });

            const storeEntryAuthCreateData = {
                storeCode: urlParams.storeCode,
                userId: userId
            };

            console.log('인증 요청 데이터:', storeEntryAuthCreateData);

            requester.storeEntryAuthCreateFromQr(storeEntryAuthCreateData, (result) => {
                if (result.code === 0) {
                    setCardAuthStatus({
                        loading: true,
                        step: 'processing',
                        message: '인증을 요청하고 있어요...'
                    });

                    // 웹소켓 메시지 전송
                    let qrAuthData = {
                        userId: userId,
                        code: "qrAuthComplete",
                        roomId: urlParams.storeCode,
                        deviceCode: urlParams.deviceCode
                    };

                    console.log('웹소켓 메시지 데이터:', qrAuthData);

                    requester.sendWSMessage(qrAuthData, (authResult) => {
                        if (authResult.code === 0) {
                            setCardAuthStatus({
                                loading: false,
                                step: 'success',
                                message: '성인인증이 완료되었어요!'
                            });

                            toast.success('인증이 완료되었습니다', {
                                autoClose: 1000, position: toast.POSITION.BOTTOM_CENTER
                            });

                            // 3초 후 창 닫기 (DoorAccessGateway와 동일)
                            setTimeout(() => {
                                closeWebView();
                            }, 3000);
                        } else {
                            throw new Error(`인증 요청 전송 실패: ${authResult.message || 'Unknown error'}`);
                        }
                    });
                } else {
                    throw new Error(`인증 생성 실패: ${result.message || 'Unknown error'}`);
                }
            });

        } catch (error) {
            console.error('카드 인증 처리 중 오류:', error);

            setCardAuthStatus({
                loading: false,
                step: 'error',
                message: `인증 처리 중 문제가 발생했어요: ${error.message}`
            });

            toast.error(`인증 중 오류: ${error.message}`, {
                autoClose: 5000, position: toast.POSITION.BOTTOM_CENTER
            });
        }
    };

    // state 유효성 검사
    useEffect(() => {
        if (!urlParams.deviceCode || !urlParams.storeCode) {
            setCardAuthStatus({
                loading: false,
                step: 'error',
                message: '잘못된 접근입니다. 올바른 경로로 접근해주세요.'
            });

            // 3초 후 메인 페이지로 리다이렉트
            setTimeout(() => {
                navigate('/', { replace: true });
            }, 3000);
        }
    }, [urlParams, navigate]);

    // DoorAccessGateway와 동일한 useEffect 패턴
    useEffect(() => {
        // state가 유효하지 않으면 실행하지 않음
        if (!urlParams.deviceCode || !urlParams.storeCode) {
            return;
        }

        if (isOurAppWebView() && cardAuthStatus.step === 'init') {
            const initializeAndProcessAuth = async () => {
                if (!isOurAppWebView()) {
                    console.log("앱 웹뷰가 아닙니다.");
                    setCardAuthStatus({
                        loading: false,
                        step: 'error',
                        message: '앱에서만 사용 가능한 기능입니다.'
                    });
                    return;
                }

                try {
                    await handleCardAuth();
                } catch (error) {
                    console.log("초기화 과정 오류:", error.message);
                    setCardAuthStatus({
                        loading: false,
                        step: 'error',
                        message: error.message || "처리 중 오류가 발생했습니다."
                    });
                }
            };

            initializeAndProcessAuth();
        }
    }, [cardAuthStatus.step, token, urlParams.deviceCode, urlParams.storeCode]); // DoorAccessGateway와 동일한 의존성 배열

    // 컴포넌트 언마운트 시 정리
    useEffect(() => {
        return () => {
            if (window.handleTokenResponse) {
                delete window.handleTokenResponse;
            }
        };
    }, []);

    return (
        <Container>
            <div style={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#ffffff",
                minHeight: "100vh",
                padding: "40px 20px",
                justifyContent: "center",
                alignItems: "center"
            }}>
                {/* 로딩 스피너 */}
                {cardAuthStatus.loading && (
                    <div style={{
                        width: "60px",
                        height: "60px",
                        border: "4px solid #f0f0f0",
                        borderTop: "4px solid var(--main-color2)",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        marginBottom: "24px"
                    }} />
                )}

                {/* 성공 아이콘 */}
                {cardAuthStatus.step === 'success' && (
                    <div style={{
                        width: "60px",
                        height: "60px",
                        backgroundColor: "#4CAF50",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "24px"
                    }}>
                        <span style={{
                            color: "white",
                            fontSize: "30px",
                            fontWeight: "bold"
                        }}>✓</span>
                    </div>
                )}

                {/* 에러 아이콘 */}
                {cardAuthStatus.step === 'error' && (
                    <div style={{
                        width: "60px",
                        height: "60px",
                        backgroundColor: "#f44336",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "24px"
                    }}>
                        <span style={{
                            color: "white",
                            fontSize: "30px",
                            fontWeight: "bold"
                        }}>!</span>
                    </div>
                )}

                <h1 style={{
                    color: "var(--main-color2)",
                    fontSize: "28px",
                    fontWeight: "700",
                    marginBottom: "16px",
                    textAlign: "center"
                }}>
                    성인인증 확인
                </h1>

                <p style={{
                    color: "#666666",
                    fontSize: "16px",
                    textAlign: "center",
                    lineHeight: "1.5",
                    marginBottom: "20px"
                }}>
                    {cardAuthStatus.message || "성인인증을 확인하고 있어요..."}
                </p>

                {/* 재시도 버튼 (에러 시에만 표시) */}
                {cardAuthStatus.step === 'error' && urlParams.deviceCode && urlParams.storeCode && (
                    <button
                        onClick={handleCardAuth}
                        style={{
                            backgroundColor: "var(--main-color2)",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            padding: "12px 24px",
                            fontSize: "16px",
                            fontWeight: "600",
                            cursor: "pointer",
                            marginTop: "16px"
                        }}
                    >
                        다시 시도
                    </button>
                )}

                {/* CSS 애니메이션 */}
                <style jsx>{`
                   @keyframes spin {
                       0% { transform: rotate(0deg); }
                       100% { transform: rotate(360deg); }
                   }
               `}</style>
            </div>
        </Container>
    );
}

export default CardAuthVerification;